import { useCallback, useEffect, useState } from "react";
import { useDeleteAsk, deletionRequested } from "@/lib/deleteApproval";
import { listScripts, createScript, uploadScript, deleteScript } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, FileText, Trash2, UploadCloud, Loader2 } from "lucide-react";

export default function Scripts() {
  const askDelete = useDeleteAsk();
  const [scripts, setScripts] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const refresh = useCallback(() => listScripts().then(setScripts), []);
  useEffect(() => { refresh(); }, [refresh]);

  const remove = async (id) => {
    const reason = askDelete("this script");
    if (reason === null) return;
    try { if (deletionRequested(await deleteScript(id, reason))) return; }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete it"); return; }
    toast.success("Script deleted");
    if (selected?.id === id) setSelected(null);
    refresh();
  };

  return (
    <div className="space-y-6" data-testid="scripts-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="eyebrow">Script library</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Official sales scripts</h2>
          <p className="text-sm text-zinc-400 mt-1">Paste them in or upload PDF / DOCX. Every recording is graded against one of these.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger data-testid="scripts-dialog-trigger-36" asChild>
            <Button className="bg-blue-600 hover:bg-blue-500 rounded-full px-5" data-testid="new-script-btn">
              <Plus className="h-4 w-4 mr-1.5" /> New script
            </Button>
          </DialogTrigger>
          <ScriptDialog onDone={() => { setOpen(false); refresh(); }} />
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 space-y-3">
          {scripts.length === 0 && (
            <div className="card-surface p-10 text-center">
              <FileText className="h-8 w-8 text-zinc-600 mx-auto" strokeWidth={1.5} />
              <h3 className="font-display text-lg font-bold mt-4">No scripts yet</h3>
              <p className="text-sm text-zinc-500 mt-1">Add your first script to start scoring calls.</p>
            </div>
          )}
          {scripts.map((s) => (
            <button
              key={s.id}
              onClick={() => setSelected(s)}
              data-testid={`script-item-${s.id}`}
              className={`w-full text-left card-surface p-4 transition-colors ${
                selected?.id === s.id ? "border-blue-500/60 bg-[#101318]" : ""
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display font-bold truncate">{s.title}</div>
                  <div className="text-xs text-zinc-500 mt-1">
                    {new Date(s.created_at).toLocaleDateString()} · {s.source.toUpperCase()} · {s.content.length.toLocaleString()} chars
                  </div>
                </div>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); remove(s.id); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); remove(s.id); } }}
                  className="text-zinc-500 hover:text-rose-400 transition-colors p-1 rounded"
                  data-testid={`delete-script-${s.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </span>
              </div>
              <p className="text-xs text-zinc-400 mt-2.5 line-clamp-2">{s.content.slice(0, 180)}</p>
            </button>
          ))}
        </div>

        <div className="lg:col-span-7">
          <div className="card-surface p-6 sticky top-24">
            {selected ? (
              <>
                <div className="eyebrow">Preview</div>
                <h3 className="font-display text-2xl font-bold tracking-tight mt-1">{selected.title}</h3>
                <div className="text-xs text-zinc-500 mt-1">Source: {selected.source.toUpperCase()}</div>
                <div className="mt-5 max-h-[60vh] overflow-y-auto pr-2 whitespace-pre-wrap text-sm text-zinc-300 leading-relaxed" data-testid="script-content-preview">
                  {selected.content}
                </div>
              </>
            ) : (
              <div className="text-center py-16 text-zinc-500 text-sm">Select a script to preview its full content.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ScriptDialog({ onDone }) {
  const [tab, setTab] = useState("paste");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Give the script a title");
    setBusy(true);
    try {
      if (tab === "paste") {
        if (!content.trim()) return toast.error("Paste the script content");
        await createScript({ title, content });
      } else {
        if (!file) return toast.error("Choose a PDF or DOCX file");
        await uploadScript(title, file);
      }
      toast.success("Script saved");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-2xl" data-testid="script-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-xl">New sales script</DialogTitle>
      </DialogHeader>
      <form data-testid="scripts-form-140" onSubmit={submit} className="space-y-4 mt-2">
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Title</Label>
          <Input
            data-testid="script-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Discovery Call — SMB v3"
            className="bg-[#0a0a0a] border-[#262626] mt-2"
          />
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[#0a0a0a] border border-[#262626]">
            <TabsTrigger value="paste" data-testid="tab-paste">Paste text</TabsTrigger>
            <TabsTrigger value="upload" data-testid="tab-upload">Upload PDF/DOCX</TabsTrigger>
          </TabsList>
          <TabsContent value="paste" className="mt-3">
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Opening… Value proposition… Discovery questions… Objection handling… Close…"
              className="bg-[#0a0a0a] border-[#262626] min-h-[240px] font-mono text-xs"
              data-testid="script-content-input"
            />
          </TabsContent>
          <TabsContent value="upload" className="mt-3">
            <label
              htmlFor="script-file"
              className="flex flex-col items-center justify-center border border-dashed border-[#333] rounded-xl px-6 py-10 cursor-pointer hover:border-blue-500/60 hover:bg-[#0d0d0d] transition-colors"
              data-testid="script-dropzone"
            >
              <UploadCloud className="h-6 w-6 text-zinc-500 mb-2" />
              <div className="text-sm text-zinc-300">{file ? file.name : "Choose a PDF or DOCX file"}</div>
              <div className="text-[11px] text-zinc-500 mt-1">We&apos;ll extract the text automatically</div>
              <input
                id="script-file"
                type="file"
                accept=".pdf,.docx,.txt"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="script-file-input"
              />
            </label>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-500 rounded-full" data-testid="script-submit-btn">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</> : "Save script"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
